// Ultimate Card Game (UCG) — Shared Faction Crest Markup
//
// The ornate per-faction crest (Human Vanguard wing-chevron insignia, Sul
// Swarm brood-pyramid insignia) originates in packOpeningModal.js's cargo
// bay booster art. The deck selector (js/ui/deckBuilder.js) reuses the
// exact same crest so a standard deck's "logo" always matches its pack —
// see the CLAUDE.md request to give every standard deck the pack's logo.
//
// `idSuffix` keeps SVG gradient ids unique whenever a crest is rendered
// more than once in the same document at the same time (e.g. the deck
// selector grid renders one crest per standard deck, while the cargo bay
// pack stays mounted in the DOM behind it) — colliding ids would make one
// instance silently borrow the other's <linearGradient>/<radialGradient>.

export function getFactionCrestMarkup(raceOrId, idSuffix = '') {
  if (raceOrId === 'Sul' || raceOrId === 'sul-crest') return sulCrestMarkup(idSuffix);
  if (raceOrId === 'Human' || raceOrId === 'human-crest') return humanCrestMarkup(idSuffix);
  if (raceOrId === 'generic-a') return diamondCrestMarkup(idSuffix);
  if (raceOrId === 'generic-b') return crossCrestMarkup(idSuffix);
  if (raceOrId === 'generic-c') return deltaCrestMarkup(idSuffix);
  if (raceOrId === 'generic-d') return hexagonCrestMarkup(idSuffix);
  if (raceOrId === 'valkyrie-aegis') return valkyrieAegisMarkup(idSuffix);
  if (raceOrId === 'void-singularity') return voidSingularityMarkup(idSuffix);
  if (raceOrId === 'brood-oculus') return broodOculusMarkup(idSuffix);
  if (raceOrId === 'foundry-titan') return foundryTitanMarkup(idSuffix);
  if (raceOrId === 'solar-crown') return solarCrownMarkup(idSuffix);
  if (raceOrId === 'ghost-protocol') return ghostProtocolMarkup(idSuffix);
  if (raceOrId === 'rejected-crown') return rejectedCrownMarkup(idSuffix);
  return humanCrestMarkup(idSuffix);
}

export function getDeckIconMarkup(iconId, idSuffix = '') {
  return getFactionCrestMarkup(iconId, idSuffix);
}

export function humanCrestMarkup(s) {
  return `
    <div class="crest-shield-wrap">
      <svg class="crest-svg" viewBox="0 0 160 140">
        <defs>
          <linearGradient id="crestGoldGradH${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#fef08a" />
            <stop offset="30%" stop-color="#eab308" />
            <stop offset="70%" stop-color="#ca8a04" />
            <stop offset="100%" stop-color="#713f12" />
          </linearGradient>
          <linearGradient id="crestWingGradH${s}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#38bdf8" />
            <stop offset="50%" stop-color="#0284c7" />
            <stop offset="100%" stop-color="#082f49" />
          </linearGradient>
          <radialGradient id="plasmaGlowH${s}" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stop-color="#ffffff" />
            <stop offset="35%" stop-color="#00e5ff" />
            <stop offset="75%" stop-color="#0284c7" />
            <stop offset="100%" stop-color="transparent" />
          </radialGradient>
        </defs>

        <g class="crest-wings">
          <path d="M 44,48 L 10,64 L 10,72 L 44,60 Z" fill="url(#crestWingGradH${s})" opacity="0.9" />
          <path d="M 44,65 L 16,80 L 16,87 L 44,76 Z" fill="url(#crestWingGradH${s})" opacity="0.8" />
          <path d="M 44,81 L 24,94 L 24,100 L 44,91 Z" fill="url(#crestWingGradH${s})" opacity="0.7" />
          <path d="M 116,48 L 150,64 L 150,72 L 116,60 Z" fill="url(#crestWingGradH${s})" opacity="0.9" />
          <path d="M 116,65 L 144,80 L 144,87 L 116,76 Z" fill="url(#crestWingGradH${s})" opacity="0.8" />
          <path d="M 116,81 L 136,94 L 136,100 L 116,91 Z" fill="url(#crestWingGradH${s})" opacity="0.7" />
        </g>

        <polygon points="80,8 126,34 126,88 80,114 34,88 34,34"
                 fill="#081724"
                 stroke="url(#crestGoldGradH${s})"
                 stroke-width="3"
                 stroke-linejoin="round" />

        <polygon points="80,16 118,38 118,84 80,106 42,84 42,38"
                 fill="#0f2334"
                 stroke="rgba(0, 229, 255, 0.45)"
                 stroke-width="1.5" />

        <circle cx="80" cy="61" r="22" fill="url(#plasmaGlowH${s})" opacity="0.9" />
        <polygon points="80,48 91,55 91,67 80,74 69,67 69,55"
                 fill="none"
                 stroke="#ffffff"
                 stroke-width="1.8" />
        <circle cx="80" cy="61" r="4.5" fill="#ffffff" />
      </svg>
    </div>
  `;
}

export function sulCrestMarkup(s) {
  return `
    <div class="crest-shield-wrap sul-crest">
      <svg class="crest-svg" viewBox="0 0 160 140">
        <defs>
          <linearGradient id="crestSulGoldGrad${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#fef08a" />
            <stop offset="50%" stop-color="#eab308" />
            <stop offset="100%" stop-color="#a16207" />
          </linearGradient>
          <linearGradient id="crestSulChitinGrad${s}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#f472b6" />
            <stop offset="40%" stop-color="#d946ef" />
            <stop offset="75%" stop-color="#7e22ce" />
            <stop offset="100%" stop-color="#3b0764" />
          </linearGradient>
          <linearGradient id="crestSulStoneLeft${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#241038" />
            <stop offset="60%" stop-color="#140622" />
            <stop offset="100%" stop-color="#0a0212" />
          </linearGradient>
          <linearGradient id="crestSulStoneRight${s}" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#1a0b28" />
            <stop offset="60%" stop-color="#0e0417" />
            <stop offset="100%" stop-color="#06010a" />
          </linearGradient>
          <radialGradient id="sulBroodGlow${s}" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stop-color="#ffffff" />
            <stop offset="25%" stop-color="#ff2a8d" />
            <stop offset="60%" stop-color="#a21caf" />
            <stop offset="85%" stop-color="#581c87" />
            <stop offset="100%" stop-color="transparent" />
          </radialGradient>
          <linearGradient id="sulCoreFlame${s}" x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stop-color="#a21caf" />
            <stop offset="45%" stop-color="#f43f5e" />
            <stop offset="80%" stop-color="#fde047" />
            <stop offset="100%" stop-color="#ffffff" />
          </linearGradient>
        </defs>

        <g class="sul-crest-mandibles">
          <path d="M 46,28 C 30,12 16,8 6,4 C 10,18 20,38 34,54 C 20,44 10,40 2,42 C 12,56 22,72 32,84 C 24,80 18,80 12,84 C 22,96 34,106 46,112 Z"
                fill="url(#crestSulChitinGrad${s})" opacity="0.95" />
          <path d="M 44,32 C 32,20 22,18 12,14 C 18,24 26,40 36,52 Z"
                fill="url(#crestSulGoldGrad${s})" opacity="0.8" />
          <path d="M 114,28 C 130,12 144,8 154,4 C 150,18 140,38 126,54 C 140,44 150,40 158,42 C 148,56 138,72 128,84 C 136,80 142,80 148,84 C 138,96 126,106 114,112 Z"
                fill="url(#crestSulChitinGrad${s})" opacity="0.95" />
          <path d="M 116,32 C 128,20 138,18 148,14 C 142,24 134,40 124,52 Z"
                fill="url(#crestSulGoldGrad${s})" opacity="0.8" />
        </g>

        <polygon points="80,10 126,116 34,116"
                 fill="#160824"
                 stroke="url(#crestSulChitinGrad${s})"
                 stroke-width="2.5"
                 stroke-linejoin="round" />

        <path d="M 80,12 L 36,114 L 75,114 L 73,88 L 68,76 L 75,60 L 71,44 L 79,28 Z"
              fill="url(#crestSulStoneLeft${s})"
              stroke="rgba(217, 70, 239, 0.4)"
              stroke-width="1" />

        <path d="M 80,12 L 124,114 L 85,114 L 87,88 L 92,76 L 85,60 L 89,44 L 81,28 Z"
              fill="url(#crestSulStoneRight${s})"
              stroke="rgba(217, 70, 239, 0.4)"
              stroke-width="1" />

        <line x1="48" y1="92" x2="73" y2="92" stroke="rgba(217, 70, 239, 0.35)" stroke-width="1" />
        <line x1="87" y1="92" x2="112" y2="92" stroke="rgba(217, 70, 239, 0.35)" stroke-width="1" />
        <line x1="56" y1="68" x2="71" y2="68" stroke="rgba(217, 70, 239, 0.35)" stroke-width="1" />
        <line x1="89" y1="68" x2="104" y2="68" stroke="rgba(217, 70, 239, 0.35)" stroke-width="1" />
        <line x1="66" y1="44" x2="74" y2="44" stroke="rgba(217, 70, 239, 0.35)" stroke-width="1" />
        <line x1="86" y1="44" x2="94" y2="44" stroke="rgba(217, 70, 239, 0.35)" stroke-width="1" />

        <polygon points="80,12 87,28 73,28"
                 fill="url(#crestSulGoldGrad${s})"
                 stroke="#f472b6"
                 stroke-width="1.2" />

        <g class="sul-glyph" opacity="0.92">
          <path d="M 52,50 Q 56,47 60,49 Q 63,52 61,56 Q 58,58 53,56 L 50,58 L 52,54 Z" fill="url(#crestSulGoldGrad${s})" />
          <line x1="57" y1="71" x2="57" y2="82" stroke="url(#crestSulGoldGrad${s})" stroke-width="1.8" />
          <line x1="53" y1="73" x2="61" y2="73" stroke="url(#crestSulGoldGrad${s})" stroke-width="1.4" />
          <line x1="53" y1="76" x2="61" y2="76" stroke="url(#crestSulGoldGrad${s})" stroke-width="1.4" />
          <line x1="53" y1="79" x2="61" y2="79" stroke="url(#crestSulGoldGrad${s})" stroke-width="1.4" />
          <path d="M 48,103 A 8,8 0 0,1 64,103 Z" fill="#3b0764" stroke="url(#crestSulGoldGrad${s})" stroke-width="1.4" />
          <line x1="46" y1="105" x2="66" y2="105" stroke="url(#crestSulGoldGrad${s})" stroke-width="1.6" />
        </g>

        <g class="sul-glyph" opacity="0.92">
          <ellipse cx="102" cy="53" rx="4.5" ry="5.5" fill="url(#crestSulGoldGrad${s})" />
          <path d="M 98,49 Q 95,46 96,44 M 106,49 Q 109,46 108,44" stroke="url(#crestSulGoldGrad${s})" stroke-width="1.2" fill="none" />
          <path d="M 96,73 L 108,73 L 108,77 L 96,77 Z" fill="none" stroke="url(#crestSulGoldGrad${s})" stroke-width="1.4" />
          <circle cx="102" cy="75" r="1.5" fill="url(#crestSulGoldGrad${s})" />
          <path d="M 95,103 A 8,8 0 0,1 111,103 Z" fill="#3b0764" stroke="url(#crestSulGoldGrad${s})" stroke-width="1.4" />
          <line x1="93" y1="105" x2="113" y2="105" stroke="url(#crestSulGoldGrad${s})" stroke-width="1.6" />
        </g>

        <path d="M 79,28 L 71,44 L 75,60 L 68,76 L 73,88 L 75,114 L 85,114 L 87,88 L 92,76 L 85,60 L 89,44 L 81,28 Z"
              fill="#0d0218" />

        <circle cx="80" cy="68" r="22" fill="url(#sulBroodGlow${s})" opacity="0.9" class="sul-fissure-glow" />

        <g class="sul-sinews" opacity="0.85">
          <path d="M 69,52 Q 78,56 89,48" stroke="#f472b6" stroke-width="1.6" fill="none" />
          <path d="M 72,62 Q 82,65 91,59" stroke="#d946ef" stroke-width="1.4" fill="none" />
          <path d="M 68,74 Q 80,72 92,78" stroke="#f472b6" stroke-width="1.6" fill="none" />
          <path d="M 72,86 Q 79,83 88,89" stroke="#d946ef" stroke-width="1.4" fill="none" />
          <path d="M 74,102 Q 80,98 86,104" stroke="#c084fc" stroke-width="1.2" fill="none" />
        </g>

        <g class="sul-core-egg">
          <path d="M 80,52 C 87,52 92,61 90,73 C 88,82 84,86 80,86 C 76,86 72,82 70,73 C 68,61 73,52 80,52 Z"
                fill="url(#sulCoreFlame${s})" />
          <ellipse cx="80" cy="69" rx="2.2" ry="7.5" fill="#ffffff" />
          <circle cx="80" cy="69" r="1.4" fill="#ff007f" />
        </g>

        <g class="sul-root-tendrils">
          <path d="M 54,115 Q 42,126 28,124 Q 38,120 50,116 Z" fill="url(#crestSulChitinGrad${s})" opacity="0.9" />
          <path d="M 70,115 Q 60,132 44,136 Q 56,128 68,116 Z" fill="url(#crestSulChitinGrad${s})" opacity="0.85" />
          <path d="M 106,115 Q 118,126 132,124 Q 122,120 110,116 Z" fill="url(#crestSulChitinGrad${s})" opacity="0.9" />
          <path d="M 90,115 Q 100,132 116,136 Q 104,128 92,116 Z" fill="url(#crestSulChitinGrad${s})" opacity="0.85" />
          <path d="M 78,114 Q 80,128 79,134 Q 82,128 82,114 Z" fill="url(#crestSulGoldGrad${s})" opacity="0.8" />
        </g>
      </svg>
    </div>
  `;
}

export function diamondCrestMarkup(s = '') {
  return `
    <div class="crest-shield-wrap diamond-crest">
      <svg class="crest-svg" viewBox="0 0 160 140">
        <defs>
          <linearGradient id="crestDiaMetal${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#f0f9ff" />
            <stop offset="30%" stop-color="#38bdf8" />
            <stop offset="70%" stop-color="#0284c7" />
            <stop offset="100%" stop-color="#082f49" />
          </linearGradient>
          <linearGradient id="crestDiaWings${s}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#7dd3fc" />
            <stop offset="50%" stop-color="#0284c7" />
            <stop offset="100%" stop-color="#031d30" />
          </linearGradient>
          <linearGradient id="crestDiaLit${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#e0f2fe" />
            <stop offset="45%" stop-color="#38bdf8" />
            <stop offset="100%" stop-color="#0369a1" />
          </linearGradient>
          <linearGradient id="crestDiaShd${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#0284c7" />
            <stop offset="55%" stop-color="#075985" />
            <stop offset="100%" stop-color="#082f49" />
          </linearGradient>
          <radialGradient id="crestDiaGlow${s}" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stop-color="#ffffff" />
            <stop offset="30%" stop-color="#00f0ff" />
            <stop offset="70%" stop-color="#0284c7" />
            <stop offset="100%" stop-color="transparent" />
          </radialGradient>
        </defs>

        <!-- Decorative Outer Chevron Stabilizers -->
        <g class="dia-decor-flanges">
          <!-- Side Winglets Left -->
          <path d="M 44,56 L 12,62 L 14,69 L 34,68 Z" fill="url(#crestDiaWings${s})" opacity="0.9" />
          <path d="M 38,70 L 6,70 L 10,76 L 36,74 Z" fill="url(#crestDiaWings${s})" opacity="0.85" />
          <path d="M 44,84 L 12,78 L 14,71 L 34,72 Z" fill="url(#crestDiaWings${s})" opacity="0.8" />
          <!-- Side Winglets Right -->
          <path d="M 116,56 L 148,62 L 146,69 L 126,68 Z" fill="url(#crestDiaWings${s})" opacity="0.9" />
          <path d="M 122,70 L 154,70 L 150,76 L 124,74 Z" fill="url(#crestDiaWings${s})" opacity="0.85" />
          <path d="M 116,84 L 148,78 L 146,71 L 126,72 Z" fill="url(#crestDiaWings${s})" opacity="0.8" />
          <!-- Top & Bottom Fin Accents -->
          <path d="M 80,6 L 92,17 L 86,19 L 80,13 L 74,19 L 68,17 Z" fill="url(#crestDiaMetal${s})" opacity="0.95" />
          <path d="M 80,134 L 92,123 L 86,121 L 80,127 L 74,121 L 68,123 Z" fill="url(#crestDiaMetal${s})" opacity="0.95" />
        </g>

        <!-- Main 3D Faceted Diamond Shield -->
        <polygon points="80,16 132,70 80,124 28,70"
                 fill="#030f1c"
                 stroke="url(#crestDiaMetal${s})"
                 stroke-width="3"
                 stroke-linejoin="round" />

        <!-- 3D Beveled Facets (Left Lit, Right Shaded) -->
        <polygon points="80,18 80,70 30,70" fill="url(#crestDiaLit${s})" opacity="0.95" />
        <polygon points="80,18 130,70 80,70" fill="url(#crestDiaLit${s})" opacity="0.65" />
        <polygon points="30,70 80,70 80,122" fill="url(#crestDiaShd${s})" opacity="0.75" />
        <polygon points="80,70 130,70 80,122" fill="url(#crestDiaShd${s})" opacity="0.95" />

        <!-- Facet Ridges -->
        <line x1="80" y1="18" x2="80" y2="122" stroke="rgba(255, 255, 255, 0.7)" stroke-width="1.4" />
        <line x1="30" y1="70" x2="130" y2="70" stroke="rgba(0, 240, 255, 0.5)" stroke-width="1.2" />

        <!-- Recessed Inner Chamber -->
        <polygon points="80,38 110,70 80,102 50,70"
                 fill="#020912"
                 stroke="rgba(0, 240, 255, 0.6)"
                 stroke-width="1.8" />

        <!-- Central Plasma Core -->
        <circle cx="80" cy="70" r="18" fill="url(#crestDiaGlow${s})" opacity="0.95" />
        <polygon points="80,55 91,70 80,85 69,70" fill="none" stroke="#ffffff" stroke-width="1.8" />
        <circle cx="80" cy="70" r="3.5" fill="#ffffff" />
      </svg>
    </div>
  `;
}

export function crossCrestMarkup(s = '') {
  return `
    <div class="crest-shield-wrap cross-crest">
      <svg class="crest-svg" viewBox="0 0 160 140">
        <defs>
          <linearGradient id="crestCrossGold${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#fef08a" />
            <stop offset="30%" stop-color="#eab308" />
            <stop offset="70%" stop-color="#ca8a04" />
            <stop offset="100%" stop-color="#713f12" />
          </linearGradient>
          <linearGradient id="crestCrossDark${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#ca8a04" />
            <stop offset="50%" stop-color="#78350f" />
            <stop offset="100%" stop-color="#2d0f03" />
          </linearGradient>
          <linearGradient id="crestCrossRay${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#fef9c3" />
            <stop offset="50%" stop-color="#f59e0b" />
            <stop offset="100%" stop-color="#451a03" />
          </linearGradient>
          <radialGradient id="crestCrossGlow${s}" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stop-color="#ffffff" />
            <stop offset="30%" stop-color="#fde047" />
            <stop offset="65%" stop-color="#ea580c" />
            <stop offset="100%" stop-color="transparent" />
          </radialGradient>
        </defs>

        <!-- Backing Alignment Ring -->
        <circle cx="80" cy="70" r="46" fill="none" stroke="url(#crestCrossGold${s})" stroke-width="1.4" stroke-dasharray="6 4" opacity="0.45" />

        <!-- 4 Diagonal Corner Energy Rays (forming Star-Cross) -->
        <g class="cross-rays">
          <path d="M 64,54 L 38,28 L 52,42 L 56,62 Z" fill="url(#crestCrossRay${s})" opacity="0.9" />
          <path d="M 96,54 L 122,28 L 108,42 L 104,62 Z" fill="url(#crestCrossRay${s})" opacity="0.9" />
          <path d="M 64,86 L 38,112 L 52,98 L 56,78 Z" fill="url(#crestCrossRay${s})" opacity="0.9" />
          <path d="M 96,86 L 122,112 L 110,98 L 104,78 Z" fill="url(#crestCrossRay${s})" opacity="0.9" />
        </g>

        <!-- 3D Beveled Cross Arms (Lit vs Shaded Facets) -->
        <!-- Top Arm -->
        <polygon points="80,70 68,22 80,14" fill="url(#crestCrossGold${s})" />
        <polygon points="80,70 80,14 92,22" fill="url(#crestCrossDark${s})" />
        <!-- Right Arm -->
        <polygon points="80,70 118,58 126,70" fill="url(#crestCrossGold${s})" />
        <polygon points="80,70 126,70 118,82" fill="url(#crestCrossDark${s})" />
        <!-- Bottom Arm -->
        <polygon points="80,70 68,118 80,126" fill="url(#crestCrossGold${s})" />
        <polygon points="80,70 80,126 92,118" fill="url(#crestCrossDark${s})" />
        <!-- Left Arm -->
        <polygon points="80,70 42,58 34,70" fill="url(#crestCrossGold${s})" />
        <polygon points="80,70 34,70 42,82" fill="url(#crestCrossDark${s})" />

        <!-- Outer Cross Contour Outline -->
        <path d="M 68,22 L 80,14 L 92,22 L 86,54 L 118,58 L 126,70 L 118,82 L 86,86 L 92,118 L 80,126 L 68,118 L 74,86 L 42,82 L 34,70 L 42,58 L 74,54 Z"
              fill="none"
              stroke="url(#crestCrossGold${s})"
              stroke-width="2.5"
              stroke-linejoin="round" />

        <!-- Ridge Lines -->
        <line x1="80" y1="14" x2="80" y2="126" stroke="rgba(255, 255, 255, 0.6)" stroke-width="1.2" />
        <line x1="34" y1="70" x2="126" y2="70" stroke="rgba(255, 255, 255, 0.6)" stroke-width="1.2" />

        <!-- Center Vault Hub -->
        <circle cx="80" cy="70" r="19" fill="#140601" stroke="url(#crestCrossGold${s})" stroke-width="2" />
        <circle cx="80" cy="70" r="14" fill="url(#crestCrossGlow${s})" opacity="0.95" />
        <polygon points="80,59 83,67 91,70 83,73 80,81 77,73 69,70 77,67" fill="#ffffff" opacity="0.9" />
        <circle cx="80" cy="70" r="2.8" fill="#ffffff" />
      </svg>
    </div>
  `;
}

export function deltaCrestMarkup(s = '') {
  return `
    <div class="crest-shield-wrap delta-crest">
      <svg class="crest-svg" viewBox="0 0 160 140">
        <defs>
          <linearGradient id="crestDeltaMint${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#a7f3d0" />
            <stop offset="35%" stop-color="#34d399" />
            <stop offset="75%" stop-color="#059669" />
            <stop offset="100%" stop-color="#064e3b" />
          </linearGradient>
          <linearGradient id="crestDeltaDark${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#047857" />
            <stop offset="50%" stop-color="#064e3b" />
            <stop offset="100%" stop-color="#022c22" />
          </linearGradient>
          <linearGradient id="crestDeltaWing${s}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#6ee7b7" />
            <stop offset="55%" stop-color="#059669" />
            <stop offset="100%" stop-color="#021c13" />
          </linearGradient>
          <radialGradient id="crestDeltaGlow${s}" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stop-color="#ffffff" />
            <stop offset="35%" stop-color="#34d399" />
            <stop offset="70%" stop-color="#059669" />
            <stop offset="100%" stop-color="transparent" />
          </radialGradient>
        </defs>

        <!-- Swept Interceptor Wings (Outer Decoration) -->
        <g class="delta-wings">
          <!-- Left Upper Winglet -->
          <path d="M 52,58 L 20,76 L 24,84 L 46,72 Z" fill="url(#crestDeltaWing${s})" opacity="0.85" />
          <!-- Left Lower Swept Wing -->
          <path d="M 38,88 L 10,106 L 14,118 L 34,112 Z" fill="url(#crestDeltaWing${s})" opacity="0.9" />
          <!-- Right Upper Winglet -->
          <path d="M 108,58 L 140,76 L 136,84 L 114,72 Z" fill="url(#crestDeltaWing${s})" opacity="0.85" />
          <!-- Right Lower Swept Wing -->
          <path d="M 122,88 L 150,106 L 146,118 L 126,112 Z" fill="url(#crestDeltaWing${s})" opacity="0.9" />
          <!-- Base Thruster Pods -->
          <path d="M 44,118 L 44,127 L 54,127 L 54,118 Z" fill="url(#crestDeltaDark${s})" />
          <path d="M 106,118 L 106,127 L 116,127 L 116,118 Z" fill="url(#crestDeltaDark${s})" />
          <polygon points="80,118 75,129 85,129" fill="url(#crestDeltaMint${s})" />
        </g>

        <!-- 3D Volumetric Kinetic Prism -->
        <!-- Left Facet (Lit) -->
        <polygon points="80,14 80,84 26,118" fill="url(#crestDeltaMint${s})" opacity="0.95" />
        <!-- Right Facet (Shaded) -->
        <polygon points="80,14 134,118 80,84" fill="url(#crestDeltaDark${s})" opacity="0.95" />
        <!-- Bottom Facet (Deep Underside) -->
        <polygon points="26,118 80,84 134,118" fill="#021c13" opacity="0.9" />

        <!-- Outer Rim Stroke -->
        <polygon points="80,14 134,118 26,118"
                 fill="none"
                 stroke="url(#crestDeltaMint${s})"
                 stroke-width="2.8"
                 stroke-linejoin="round" />

        <!-- Center Spine Ridge -->
        <line x1="80" y1="14" x2="80" y2="84" stroke="rgba(255, 255, 255, 0.75)" stroke-width="1.4" />

        <!-- Recessed Inverted Delta Core Chamber -->
        <polygon points="80,98 62,64 98,64" fill="#01140d" stroke="rgba(52, 211, 153, 0.8)" stroke-width="1.8" />

        <!-- Reactor Core Glow -->
        <circle cx="80" cy="74" r="16" fill="url(#crestDeltaGlow${s})" opacity="0.95" />
        <polygon points="80,66 87,78 73,78" fill="#ffffff" opacity="0.9" />
        <circle cx="80" cy="74" r="2.5" fill="#ffffff" />
      </svg>
    </div>
  `;
}

export function hexagonCrestMarkup(s = '') {
  return `
    <div class="crest-shield-wrap hex-crest">
      <svg class="crest-svg" viewBox="0 0 160 140">
        <defs>
          <linearGradient id="crestHexViolet${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#f5d0fe" />
            <stop offset="30%" stop-color="#c084fc" />
            <stop offset="70%" stop-color="#7e22ce" />
            <stop offset="100%" stop-color="#3b0764" />
          </linearGradient>
          <linearGradient id="crestHexDark${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#7e22ce" />
            <stop offset="55%" stop-color="#4c1d95" />
            <stop offset="100%" stop-color="#1b0533" />
          </linearGradient>
          <linearGradient id="crestHexWing${s}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#e879f9" />
            <stop offset="50%" stop-color="#6b21a8" />
            <stop offset="100%" stop-color="#16032b" />
          </linearGradient>
          <radialGradient id="crestHexGlow${s}" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stop-color="#ffffff" />
            <stop offset="35%" stop-color="#f472b6" />
            <stop offset="70%" stop-color="#a855f7" />
            <stop offset="100%" stop-color="transparent" />
          </radialGradient>
        </defs>

        <!-- Stepped Heavy Armor Projector Fins (Flanks) -->
        <g class="hex-fins">
          <!-- Left Fins -->
          <path d="M 34,46 L 10,48 L 12,54 L 34,53 Z" fill="url(#crestHexWing${s})" opacity="0.85" />
          <path d="M 34,67 L 6,69 L 8,76 L 34,75 Z" fill="url(#crestHexWing${s})" opacity="0.95" />
          <path d="M 34,88 L 14,89 L 16,95 L 34,94 Z" fill="url(#crestHexWing${s})" opacity="0.8" />
          <!-- Right Fins -->
          <path d="M 126,46 L 150,48 L 148,54 L 126,53 Z" fill="url(#crestHexWing${s})" opacity="0.85" />
          <path d="M 126,67 L 154,69 L 152,76 L 126,75 Z" fill="url(#crestHexWing${s})" opacity="0.95" />
          <path d="M 126,88 L 146,89 L 144,95 L 126,94 Z" fill="url(#crestHexWing${s})" opacity="0.8" />
          <!-- Top & Bottom Lock Clamps -->
          <polygon points="72,12 80,6 88,12 80,16" fill="url(#crestHexViolet${s})" />
          <polygon points="72,128 80,134 88,128 80,124" fill="url(#crestHexDark${s})" />
        </g>

        <!-- 3D Beveled 6-Faceted Hexagon Citadel -->
        <!-- Top-Left Facet (Bright Highlight) -->
        <polygon points="34,42 80,16 80,70" fill="url(#crestHexViolet${s})" opacity="0.95" />
        <!-- Top-Right Facet (Upper Midtone) -->
        <polygon points="80,16 126,42 80,70" fill="url(#crestHexViolet${s})" opacity="0.75" />
        <!-- Mid-Left Facet -->
        <polygon points="34,98 34,42 80,70" fill="url(#crestHexDark${s})" opacity="0.8" />
        <!-- Mid-Right Facet -->
        <polygon points="126,42 126,98 80,70" fill="url(#crestHexDark${s})" opacity="0.9" />
        <!-- Bottom-Left Facet -->
        <polygon points="80,124 34,98 80,70" fill="url(#crestHexDark${s})" opacity="0.95" />
        <!-- Bottom-Right Facet (Deep Shadow) -->
        <polygon points="126,98 80,124 80,70" fill="#140224" opacity="0.95" />

        <!-- Outer Hexagon Rim -->
        <polygon points="80,16 126,42 126,98 80,124 34,98 34,42"
                 fill="none"
                 stroke="url(#crestHexViolet${s})"
                 stroke-width="2.8"
                 stroke-linejoin="round" />

        <!-- Facet Edge Lines -->
        <line x1="80" y1="16" x2="80" y2="70" stroke="rgba(255, 255, 255, 0.7)" stroke-width="1.2" />
        <line x1="34" y1="42" x2="80" y2="70" stroke="rgba(255, 255, 255, 0.5)" stroke-width="1.2" />
        <line x1="126" y1="42" x2="80" y2="70" stroke="rgba(240, 171, 252, 0.4)" stroke-width="1" />
        <line x1="34" y1="98" x2="80" y2="70" stroke="rgba(192, 132, 252, 0.4)" stroke-width="1" />
        <line x1="126" y1="98" x2="80" y2="70" stroke="rgba(147, 51, 234, 0.4)" stroke-width="1" />
        <line x1="80" y1="124" x2="80" y2="70" stroke="rgba(88, 28, 135, 0.6)" stroke-width="1" />

        <!-- Concentric Inner Hexagon Core Vault -->
        <polygon points="80,38 108,54 108,86 80,102 52,86 52,54"
                 fill="#080112"
                 stroke="rgba(240, 171, 252, 0.7)"
                 stroke-width="1.8" />

        <!-- Central Plasma Reactor -->
        <circle cx="80" cy="70" r="18" fill="url(#crestHexGlow${s})" opacity="0.95" />
        <polygon points="80,57 91,63 91,77 80,83 69,77 69,63" fill="none" stroke="#ffffff" stroke-width="1.8" />
        <circle cx="80" cy="70" r="3.5" fill="#ffffff" />
      </svg>
    </div>
  `;
}

// 7 NEUE HIGH-PLASTICITY FLÄCHEN-DESIGNS (Planes over Lines)

export function valkyrieAegisMarkup(s) {
  return `
    <div class="crest-shield-wrap valkyrie-crest">
      <svg class="crest-svg" viewBox="0 0 160 140">
        <defs>
          <linearGradient id="valkGold${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#fef08a" />
            <stop offset="35%" stop-color="#eab308" />
            <stop offset="100%" stop-color="#854d0e" />
          </linearGradient>
          <linearGradient id="valkTitanLit${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#ffffff" />
            <stop offset="40%" stop-color="#cbd5e1" />
            <stop offset="100%" stop-color="#64748b" />
          </linearGradient>
          <linearGradient id="valkTitanDark${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#475569" />
            <stop offset="60%" stop-color="#1e293b" />
            <stop offset="100%" stop-color="#09101d" />
          </linearGradient>
          <linearGradient id="valkWingGrad${s}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#38bdf8" />
            <stop offset="50%" stop-color="#0284c7" />
            <stop offset="100%" stop-color="#082f49" />
          </linearGradient>
          <radialGradient id="valkIonGlow${s}" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stop-color="#ffffff" />
            <stop offset="35%" stop-color="#00f0ff" />
            <stop offset="75%" stop-color="#0284c7" />
            <stop offset="100%" stop-color="transparent" />
          </radialGradient>
        </defs>

        <g class="valk-flank-planes">
          <path d="M 44,46 L 8,26 L 12,36 L 44,56 Z" fill="url(#valkWingGrad${s})" opacity="0.9" />
          <path d="M 42,64 L 6,50 L 10,60 L 42,72 Z" fill="url(#valkWingGrad${s})" opacity="0.8" />
          <path d="M 44,82 L 14,82 L 18,90 L 44,88 Z" fill="url(#valkWingGrad${s})" opacity="0.7" />
          <path d="M 116,46 L 152,26 L 148,36 L 116,56 Z" fill="url(#valkWingGrad${s})" opacity="0.9" />
          <path d="M 118,64 L 154,50 L 150,60 L 118,72 Z" fill="url(#valkWingGrad${s})" opacity="0.8" />
          <path d="M 116,82 L 146,82 L 142,90 L 116,88 Z" fill="url(#valkWingGrad${s})" opacity="0.7" />
        </g>

        <polygon points="80,10 126,48 80,122 34,48"
                 fill="none"
                 stroke="url(#valkGold${s})"
                 stroke-width="3"
                 stroke-linejoin="round" />

        <polygon points="80,12 80,118 36,50" fill="url(#valkTitanLit${s})" opacity="0.95" />
        <polygon points="80,12 124,50 80,118" fill="url(#valkTitanDark${s})" opacity="0.95" />

        <polygon points="80,32 108,54 80,94 52,54"
                 fill="#061220"
                 stroke="rgba(0, 240, 255, 0.45)"
                 stroke-width="1.6" />

        <circle cx="80" cy="62" r="20" fill="url(#valkIonGlow${s})" opacity="0.9" />
        <polygon points="80,48 90,62 80,74 70,62" fill="none" stroke="#ffffff" stroke-width="1.8" />
        <polygon points="80,53 86,62 80,70 74,62" fill="#ffffff" />
        <circle cx="80" cy="62" r="3.2" fill="#0284c7" />
      </svg>
    </div>
  `;
}

export function voidSingularityMarkup(s) {
  return `
    <div class="crest-shield-wrap void-crest">
      <svg class="crest-svg" viewBox="0 0 160 140">
        <defs>
          <linearGradient id="voidSilverTrim${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#ffffff" />
            <stop offset="45%" stop-color="#94a3b8" />
            <stop offset="100%" stop-color="#334155" />
          </linearGradient>
          <linearGradient id="voidCobaltLit${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#93c5fd" />
            <stop offset="35%" stop-color="#3b82f6" />
            <stop offset="70%" stop-color="#2563eb" />
            <stop offset="100%" stop-color="#1d4ed8" />
          </linearGradient>
          <linearGradient id="voidCobaltDark${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#1e3a8a" />
            <stop offset="60%" stop-color="#0f172a" />
            <stop offset="100%" stop-color="#050814" />
          </linearGradient>
          <linearGradient id="voidPylonBlue${s}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#60a5fa" />
            <stop offset="50%" stop-color="#2563eb" />
            <stop offset="100%" stop-color="#0a1228" />
          </linearGradient>
          <radialGradient id="voidHorizonGlow${s}" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stop-color="#ffffff" />
            <stop offset="30%" stop-color="#38bdf8" />
            <stop offset="65%" stop-color="#2563eb" />
            <stop offset="100%" stop-color="transparent" />
          </radialGradient>
        </defs>

        <g class="void-crescent-flanges">
          <path d="M 46,38 C 24,20 12,22 4,34 C 16,52 32,64 46,70 Z" fill="url(#voidPylonBlue${s})" opacity="0.95" />
          <path d="M 46,102 C 24,120 12,118 4,106 C 16,88 32,76 46,70 Z" fill="url(#voidPylonBlue${s})" opacity="0.85" />
          <path d="M 114,38 C 136,20 148,22 156,34 C 144,52 128,64 114,70 Z" fill="url(#voidPylonBlue${s})" opacity="0.95" />
          <path d="M 114,102 C 136,120 148,118 156,106 C 144,88 128,76 114,70 Z" fill="url(#voidPylonBlue${s})" opacity="0.85" />
        </g>

        <polygon points="34,34 80,12 80,70 34,70" fill="url(#voidCobaltLit${s})" opacity="0.95" />
        <polygon points="80,12 126,34 126,70 80,70" fill="url(#voidCobaltLit${s})" opacity="0.75" />
        <polygon points="34,70 80,70 80,128 34,106" fill="url(#voidCobaltDark${s})" opacity="0.85" />
        <polygon points="80,70 126,70 126,106 80,128" fill="url(#voidCobaltDark${s})" opacity="0.98" />

        <polygon points="80,12 126,34 126,106 80,128 34,106 34,34"
                 fill="none"
                 stroke="url(#voidSilverTrim${s})"
                 stroke-width="2.8"
                 stroke-linejoin="round" />

        <circle cx="80" cy="70" r="32" fill="#02040c" stroke="rgba(56, 189, 248, 0.55)" stroke-width="1.8" />
        <circle cx="80" cy="70" r="24" fill="url(#voidHorizonGlow${s})" opacity="0.95" />
        <circle cx="80" cy="70" r="11" fill="#000000" stroke="#ffffff" stroke-width="1.8" />
        <circle cx="80" cy="70" r="3" fill="#ffffff" />
      </svg>
    </div>
  `;
}

export function broodOculusMarkup(s) {
  return `
    <div class="crest-shield-wrap brood-crest">
      <svg class="crest-svg" viewBox="0 0 160 140">
        <defs>
          <linearGradient id="broodGold${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#fef08a" />
            <stop offset="50%" stop-color="#eab308" />
            <stop offset="100%" stop-color="#a16207" />
          </linearGradient>
          <linearGradient id="broodChitin${s}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#f472b6" />
            <stop offset="40%" stop-color="#d946ef" />
            <stop offset="75%" stop-color="#7e22ce" />
            <stop offset="100%" stop-color="#3b0764" />
          </linearGradient>
          <linearGradient id="broodStoneLeft${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#2a083b" />
            <stop offset="60%" stop-color="#160222" />
            <stop offset="100%" stop-color="#08000d" />
          </linearGradient>
          <linearGradient id="broodStoneRight${s}" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#1d0529" />
            <stop offset="60%" stop-color="#0e0117" />
            <stop offset="100%" stop-color="#040008" />
          </linearGradient>
          <radialGradient id="broodEyeGlow${s}" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stop-color="#ffffff" />
            <stop offset="25%" stop-color="#ff2a8d" />
            <stop offset="60%" stop-color="#a21caf" />
            <stop offset="85%" stop-color="#581c87" />
            <stop offset="100%" stop-color="transparent" />
          </radialGradient>
          <linearGradient id="broodIrisFlame${s}" x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stop-color="#9333ea" />
            <stop offset="40%" stop-color="#f43f5e" />
            <stop offset="80%" stop-color="#fde047" />
            <stop offset="100%" stop-color="#ffffff" />
          </linearGradient>
        </defs>

        <g class="brood-mandibles">
          <path d="M 46,26 C 28,10 12,8 4,16 C 12,32 24,52 38,66 C 24,56 12,54 4,60 C 16,76 28,92 42,102 Z"
                fill="url(#broodChitin${s})" opacity="0.95" />
          <path d="M 42,32 C 30,20 18,18 10,24 C 18,36 26,50 36,62 Z"
                fill="url(#broodGold${s})" opacity="0.8" />
          <path d="M 114,26 C 132,10 148,8 156,16 C 148,32 136,52 122,66 C 136,56 148,54 156,60 C 144,76 132,92 118,102 Z"
                fill="url(#broodChitin${s})" opacity="0.95" />
          <path d="M 118,32 C 130,20 142,18 150,24 C 142,36 134,50 124,62 Z"
                fill="url(#broodGold${s})" opacity="0.8" />
        </g>

        <polygon points="80,10 126,116 34,116"
                 fill="#160824"
                 stroke="url(#broodChitin${s})"
                 stroke-width="2.5"
                 stroke-linejoin="round" />

        <path d="M 80,12 L 36,114 L 75,114 L 73,70 L 78,28 Z"
              fill="url(#broodStoneLeft${s})" />
        <path d="M 80,12 L 124,114 L 85,114 L 87,70 L 82,28 Z"
              fill="url(#broodStoneRight${s})" />

        <polygon points="80,12 87,28 73,28" fill="url(#broodGold${s})" stroke="#f472b6" stroke-width="1.2" />

        <path d="M 78,28 L 73,70 L 75,114 L 85,114 L 87,70 L 82,28 Z" fill="#090010" />

        <circle cx="80" cy="70" r="24" fill="url(#broodEyeGlow${s})" opacity="0.9" />

        <path d="M 80,54 C 88,54 93,63 91,75 C 89,84 85,88 80,88 C 75,88 71,84 69,75 C 67,63 72,54 80,54 Z"
              fill="url(#broodIrisFlame${s})" />
        <ellipse cx="80" cy="71" rx="2.2" ry="8" fill="#ffffff" />
        <circle cx="80" cy="71" r="1.4" fill="#ff007f" />

        <g class="brood-tendrils">
          <path d="M 54,115 Q 42,128 26,124 Q 38,120 50,116 Z" fill="url(#broodChitin${s})" opacity="0.9" />
          <path d="M 106,115 Q 118,128 134,124 Q 122,120 110,116 Z" fill="url(#broodChitin${s})" opacity="0.9" />
          <path d="M 78,114 Q 80,132 79,138 Q 82,132 82,114 Z" fill="url(#broodGold${s})" opacity="0.85" />
        </g>
      </svg>
    </div>
  `;
}

export function foundryTitanMarkup(s) {
  return `
    <div class="crest-shield-wrap foundry-crest">
      <svg class="crest-svg" viewBox="0 0 160 140">
        <defs>
          <linearGradient id="titanHazardLit${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#fef08a" />
            <stop offset="35%" stop-color="#facc15" />
            <stop offset="70%" stop-color="#ca8a04" />
            <stop offset="100%" stop-color="#854d0e" />
          </linearGradient>
          <linearGradient id="titanHazardDark${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#a16207" />
            <stop offset="45%" stop-color="#451a03" />
            <stop offset="100%" stop-color="#0f172a" />
          </linearGradient>
          <linearGradient id="titanCastIronFlange${s}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#facc15" />
            <stop offset="30%" stop-color="#475569" />
            <stop offset="100%" stop-color="#0f172a" />
          </linearGradient>
          <radialGradient id="titanFurnaceGlow${s}" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stop-color="#ffffff" />
            <stop offset="30%" stop-color="#fde047" />
            <stop offset="70%" stop-color="#ea580c" />
            <stop offset="100%" stop-color="transparent" />
          </radialGradient>
        </defs>

        <g class="titan-piston-planes">
          <path d="M 40,46 L 10,34 L 14,44 L 38,52 Z" fill="url(#titanCastIronFlange${s})" opacity="0.9" />
          <path d="M 36,66 L 6,66 L 8,76 L 36,74 Z" fill="url(#titanCastIronFlange${s})" opacity="0.95" />
          <path d="M 40,86 L 10,98 L 14,106 L 38,96 Z" fill="url(#titanCastIronFlange${s})" opacity="0.85" />

          <path d="M 120,46 L 150,34 L 146,44 L 122,52 Z" fill="url(#titanCastIronFlange${s})" opacity="0.9" />
          <path d="M 124,66 L 154,66 L 152,76 L 124,74 Z" fill="url(#titanCastIronFlange${s})" opacity="0.95" />
          <path d="M 120,86 L 150,98 L 146,106 L 122,96 Z" fill="url(#titanCastIronFlange${s})" opacity="0.85" />
        </g>

        <path d="M 72,16 L 88,16 L 92,28 L 104,22 L 114,32 L 108,44 L 120,48 L 120,62 L 132,66 L 132,80 L 120,84 L 120,98 L 108,102 L 114,114 L 104,124 L 92,118 L 88,130 L 72,130 L 68,118 L 56,124 L 46,114 L 52,102 L 40,98 L 40,84 L 28,80 L 28,66 L 40,62 L 40,48 L 52,44 L 46,32 L 56,22 L 68,28 Z"
              fill="url(#titanHazardLit${s})"
              stroke="url(#titanHazardDark${s})"
              stroke-width="2.6"
              stroke-linejoin="round" />

        <path d="M 80,16 L 88,16 L 92,28 L 104,22 L 114,32 L 108,44 L 120,48 L 120,62 L 132,66 L 132,80 L 120,84 L 120,98 L 108,102 L 114,114 L 104,124 L 92,118 L 88,130 L 80,130 Z"
              fill="url(#titanHazardDark${s})"
              opacity="0.8" />

        <circle cx="80" cy="73" r="38" fill="#0d111a" stroke="url(#titanHazardLit${s})" stroke-width="2.2" />

        <circle cx="80" cy="73" r="26" fill="url(#titanFurnaceGlow${s})" opacity="0.95" />

        <polygon points="80,53 86,67 100,73 86,79 80,93 74,79 60,73 74,67"
                 fill="#1e293b"
                 stroke="url(#titanHazardLit${s})"
                 stroke-width="1.8" />
        <circle cx="80" cy="73" r="4.5" fill="#ffffff" />
      </svg>
    </div>
  `;
}

export function solarCrownMarkup(s) {
  return `
    <div class="crest-shield-wrap solar-crest">
      <svg class="crest-svg" viewBox="0 0 160 140">
        <defs>
          <linearGradient id="solarGoldLit${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#ffffff" />
            <stop offset="30%" stop-color="#fef08a" />
            <stop offset="70%" stop-color="#f59e0b" />
            <stop offset="100%" stop-color="#b45309" />
          </linearGradient>
          <linearGradient id="solarGoldDark${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#b45309" />
            <stop offset="55%" stop-color="#78350f" />
            <stop offset="100%" stop-color="#2a0c02" />
          </linearGradient>
          <linearGradient id="solarRubyLit${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#fca5a5" />
            <stop offset="35%" stop-color="#ef4444" />
            <stop offset="75%" stop-color="#dc2626" />
            <stop offset="100%" stop-color="#7f1d1d" />
          </linearGradient>
          <linearGradient id="solarRubyDark${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#991b1b" />
            <stop offset="55%" stop-color="#550909" />
            <stop offset="100%" stop-color="#1e0202" />
          </linearGradient>
          <radialGradient id="solarFusionGlow${s}" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stop-color="#ffffff" />
            <stop offset="25%" stop-color="#fef08a" />
            <stop offset="60%" stop-color="#ea580c" />
            <stop offset="85%" stop-color="#b91c1c" />
            <stop offset="100%" stop-color="transparent" />
          </radialGradient>
        </defs>

        <g class="solar-corona-blades">
          <polygon points="44,34 8,16 26,46 44,54" fill="url(#solarGoldLit${s})" opacity="0.95" />
          <polygon points="42,66 4,76 24,96 42,88" fill="url(#solarGoldLit${s})" opacity="0.85" />
          <polygon points="116,34 152,16 134,46 116,54" fill="url(#solarGoldDark${s})" opacity="0.9" />
          <polygon points="118,66 156,76 136,96 118,88" fill="url(#solarGoldDark${s})" opacity="0.8" />
          <polygon points="80,4 68,26 80,26" fill="url(#solarGoldLit${s})" />
          <polygon points="80,4 80,26 92,26" fill="url(#solarGoldDark${s})" />
          <polygon points="80,136 68,114 80,114" fill="url(#solarGoldLit${s})" />
          <polygon points="80,136 80,114 92,114" fill="url(#solarGoldDark${s})" />
        </g>

        <polygon points="80,14 38,34 24,70 42,106 80,126 80,70" fill="url(#solarRubyLit${s})" opacity="0.98" />
        <polygon points="80,14 80,70 80,126 118,106 136,70 122,34" fill="url(#solarRubyDark${s})" opacity="0.98" />

        <polygon points="80,14 122,34 136,70 118,106 80,126 42,106 24,70 38,34"
                 fill="none"
                 stroke="url(#solarGoldLit${s})"
                 stroke-width="2.2"
                 stroke-linejoin="round" />

        <polygon points="80,32 50,70 80,70" fill="url(#solarGoldLit${s})" opacity="0.98" />
        <polygon points="80,32 80,70 110,70" fill="url(#solarGoldLit${s})" opacity="0.75" />
        <polygon points="50,70 80,108 80,70" fill="url(#solarGoldDark${s})" opacity="0.8" />
        <polygon points="80,70 80,108 110,70" fill="url(#solarGoldDark${s})" opacity="0.98" />

        <polygon points="80,48 96,59 96,81 80,92 64,81 64,59" fill="#140202" />

        <circle cx="80" cy="70" r="18" fill="url(#solarFusionGlow${s})" opacity="0.98" />

        <polygon points="80,54 83,67 80,70 77,67" fill="#ffffff" />
        <polygon points="80,86 83,73 80,70 77,73" fill="#fde047" />
        <polygon points="64,70 77,67 80,70 77,73" fill="#ffffff" />
        <polygon points="96,70 83,67 80,70 83,73" fill="#fde047" />
        <circle cx="80" cy="70" r="3.5" fill="#ffffff" />
      </svg>
    </div>
  `;
}

export function ghostProtocolMarkup(s) {
  return `
    <div class="crest-shield-wrap ghost-crest">
      <svg class="crest-svg" viewBox="0 0 160 140">
        <defs>
          <linearGradient id="ghostSteelLit${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#ffffff" />
            <stop offset="40%" stop-color="#cbd5e1" />
            <stop offset="100%" stop-color="#64748b" />
          </linearGradient>
          <linearGradient id="ghostSteelDark${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#475569" />
            <stop offset="60%" stop-color="#1e293b" />
            <stop offset="100%" stop-color="#080e1a" />
          </linearGradient>
          <linearGradient id="ghostWingGrad${s}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#38bdf8" />
            <stop offset="50%" stop-color="#0284c7" />
            <stop offset="100%" stop-color="#082f49" />
          </linearGradient>
          <radialGradient id="ghostSensorGlow${s}" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stop-color="#ffffff" />
            <stop offset="35%" stop-color="#00f0ff" />
            <stop offset="70%" stop-color="#0284c7" />
            <stop offset="100%" stop-color="transparent" />
          </radialGradient>
        </defs>

        <g class="ghost-flank-planes">
          <path d="M 40,48 L 12,44 L 14,54 L 38,56 Z" fill="url(#ghostWingGrad${s})" opacity="0.9" />
          <path d="M 36,68 L 6,68 L 8,78 L 34,76 Z" fill="url(#ghostWingGrad${s})" opacity="0.95" />
          <path d="M 40,88 L 14,92 L 16,102 L 38,96 Z" fill="url(#ghostWingGrad${s})" opacity="0.85" />

          <path d="M 120,48 L 148,44 L 146,54 L 122,56 Z" fill="url(#ghostWingGrad${s})" opacity="0.9" />
          <path d="M 124,68 L 154,68 L 152,78 L 126,76 Z" fill="url(#ghostWingGrad${s})" opacity="0.95" />
          <path d="M 120,88 L 146,92 L 144,102 L 122,96 Z" fill="url(#ghostWingGrad${s})" opacity="0.85" />
        </g>

        <polygon points="80,12 80,66 36,54 42,22" fill="url(#ghostSteelLit${s})" opacity="0.95" />
        <polygon points="80,12 118,22 124,54 80,66" fill="url(#ghostSteelDark${s})" opacity="0.95" />

        <polygon points="80,12 118,22 124,54 80,66 36,54 42,22"
                 fill="none"
                 stroke="url(#ghostSteelLit${s})"
                 stroke-width="2.6"
                 stroke-linejoin="round" />

        <polygon points="36,54 26,82 50,96 58,70" fill="url(#ghostSteelLit${s})" opacity="0.85" />
        <polygon points="124,54 102,70 110,96 134,82" fill="url(#ghostSteelDark${s})" opacity="0.85" />

        <polygon points="40,56 120,56 112,78 48,78" fill="#020813" stroke="rgba(0, 240, 255, 0.45)" stroke-width="1.6" />

        <circle cx="62" cy="67" r="11" fill="url(#ghostSensorGlow${s})" opacity="0.95" />
        <circle cx="62" cy="67" r="4.5" fill="#ffffff" />
        <circle cx="98" cy="67" r="11" fill="url(#ghostSensorGlow${s})" opacity="0.95" />
        <circle cx="98" cy="67" r="4.5" fill="#ffffff" />

        <polygon points="56,88 104,88 96,128 64,128"
                 fill="#080f1e"
                 stroke="url(#ghostSteelLit${s})"
                 stroke-width="2.2" />
        <rect x="68" y="98" width="24" height="4" rx="1.5" fill="#00f0ff" opacity="0.85" />
        <rect x="70" y="110" width="20" height="4" rx="1.5" fill="#00f0ff" opacity="0.75" />
      </svg>
    </div>
  `;
}

export function rejectedCrownMarkup(s) {
  return `
    <div class="crest-shield-wrap rejected-crest">
      <svg class="crest-svg" viewBox="0 0 160 140">
        <defs>
          <linearGradient id="rejcChitinLit${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#f5d0fe" />
            <stop offset="45%" stop-color="#c084fc" />
            <stop offset="100%" stop-color="#7e22ce" />
          </linearGradient>
          <linearGradient id="rejcChitinDark${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#7e22ce" />
            <stop offset="60%" stop-color="#4c1d95" />
            <stop offset="100%" stop-color="#1e0538" />
          </linearGradient>
          <linearGradient id="rejcBoneLit${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#ffffff" />
            <stop offset="45%" stop-color="#f1f5f9" />
            <stop offset="100%" stop-color="#cbd5e1" />
          </linearGradient>
          <linearGradient id="rejcBoneDark${s}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#64748b" />
            <stop offset="55%" stop-color="#334155" />
            <stop offset="100%" stop-color="#0f172a" />
          </linearGradient>
          <linearGradient id="rejcGoldTrim${s}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#fef08a" />
            <stop offset="50%" stop-color="#facc15" />
            <stop offset="100%" stop-color="#b45309" />
          </linearGradient>
          <radialGradient id="rejcSoulGlow${s}" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stop-color="#ffffff" />
            <stop offset="35%" stop-color="#fde047" />
            <stop offset="70%" stop-color="#f472b6" />
            <stop offset="100%" stop-color="transparent" />
          </radialGradient>
        </defs>

        <g class="rejected-horn-plates">
          <polygon points="46,26 12,12 28,42 46,46" fill="url(#rejcChitinLit${s})" opacity="0.95" />
          <polygon points="44,54 8,60 26,84 44,78" fill="url(#rejcChitinDark${s})" opacity="0.95" />
          <polygon points="114,26 148,12 132,42 114,46" fill="url(#rejcChitinLit${s})" opacity="0.8" />
          <polygon points="116,54 152,60 134,84 116,78" fill="url(#rejcChitinDark${s})" opacity="0.85" />
        </g>

        <polygon points="80,14 112,30 122,64 110,96 80,118 50,96 38,64 48,30"
                 fill="none"
                 stroke="url(#rejcChitinLit${s})"
                 stroke-width="2.2"
                 stroke-linejoin="round" />

        <polygon points="80,14 48,30 38,64 50,96 80,118" fill="url(#rejcBoneLit${s})" opacity="0.98" />
        <polygon points="80,14 80,118 110,96 122,64 112,30" fill="url(#rejcBoneDark${s})" opacity="0.98" />

        <polygon points="80,28 73,40 80,52" fill="#fef08a" />
        <polygon points="80,28 87,40 80,52" fill="#ca8a04" />

        <polygon points="62,56 73,68 62,80 51,68" fill="#07010f" />
        <polygon points="98,56 109,68 98,80 87,68" fill="#07010f" />

        <circle cx="62" cy="68" r="9" fill="url(#rejcSoulGlow${s})" opacity="0.95" />
        <circle cx="98" cy="68" r="9" fill="url(#rejcSoulGlow${s})" opacity="0.95" />

        <polygon points="62,64 66,68 62,72 58,68" fill="#ffffff" />
        <polygon points="98,64 102,68 98,72 94,68" fill="#ffffff" />

        <path d="M 58,106 L 46,126 L 64,116 L 80,132 L 96,116 L 114,126 L 102,106 Z"
              fill="url(#rejcChitinDark${s})" />
        <polygon points="76,114 80,132 84,114" fill="url(#rejcGoldTrim${s})" />
      </svg>
    </div>
  `;
}

